import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createClientYuq } from 'utils/validate';
import { useCallback, useRef } from 'react';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { createClient } from 'services/clients';
import { useFlash } from 'contexts/flash';
import { uploadLogoClient } from 'services/logo';
export type FormCreateClient = {
  clientName: string;
  clientEmail: string;
  contactPerson: string;
  fileLogo?: File;
};

export const useInjection = () => {
  const navigate = useNavigate();
  const fileInputIconRef = useRef<HTMLInputElement>(null);

  const { setFlash } = useFlash();
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormCreateClient>({ resolver: yupResolver(createClientYuq) });

  const handleCreateClient = useCallback(
    async (value: FormCreateClient) => {
      const data = await createClient(value);
      if (data?.client?.id && value.fileLogo) {
        const formData = new FormData();
        const blob = new Blob([value.fileLogo], { type: value.fileLogo?.type });
        formData.append('file', blob, value.fileLogo?.name);
        await uploadLogoClient(data.client.id, formData);
      }
      setFlash({ type: 'success', message: 'Create client successful' });
      navigate('/clients');
    },
    [navigate, setFlash]
  );

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    setValue('fileLogo', fileList[0]);
  };

  const handleFileClick = useCallback(() => {
    if (fileInputIconRef.current) {
      fileInputIconRef.current.click();
    }
  }, []);

  const handleDeleteLogo = useCallback(async () => {
    setValue('fileLogo', undefined);
  }, [setValue]);

  const { asyncCallback } = useAsyncCallback(handleCreateClient, []);
  return {
    backToClient: () => navigate('client'),
    handleSubmit,
    register,
    errors,
    onCreateClient: asyncCallback,
    fileInputIconRef,
    handleFileChange,
    fileIcon: watch('fileLogo'),
    handleFileClick,
    handleDeleteLogo
  };
};
